import { Language } from '@/context/LanguageContext';

/**
 * Extract language from a pathname
 * @param pathname - The current pathname (e.g., /es/city/madrid)
 * @returns The extracted language code or null if not found
 */
export const extractLanguageFromPath = (pathname: string): Language | null => {
  const validLanguages: Language[] = ['en', 'es', 'fr', 'de', 'it', 'pt', 'da', 'no', 'fi', 'sv', 'is', 'cs', 'sk', 'sl', 'pl', 'et', 'lv', 'lt', 'el', 'nl', 'ko', 'ja', 'zh', 'ar', 'hi', 'ru', 'tr', 'id', 'ro', 'bg', 'hr', 'hu', 'uk', 'he', 'th', 'vi', 'ms', 'tl', 'fa', 'bn', 'ur', 'sw'];
  
  const pathSegments = pathname.split('/').filter(Boolean);
  const potentialLang = pathSegments[0];
  
  return validLanguages.includes(potentialLang as Language) ? potentialLang as Language : null;
};

/**
 * Get the canonical English path from a localized path
 * @param pathname - The localized pathname (e.g., /es/city/madrid)
 * @returns The canonical English path (e.g., /city/madrid)
 */
export const getCanonicalPath = (pathname: string): string => {
  const language = extractLanguageFromPath(pathname);
  
  if (!language || language === 'en') {
    return pathname;
  }
  
  // Remove language prefix: /es/city/madrid -> /city/madrid
  return pathname.replace(`/${language}`, '') || '/';
};

/**
 * Get the localized path for a given language
 * @param pathname - The base pathname (e.g., /city/madrid)
 * @param language - The target language
 * @returns The localized path (e.g., /es/city/madrid)
 */
export const getLocalizedPath = (pathname: string, language: Language): string => {
  if (language === 'en') {
    return pathname;
  }
  
  return `/${language}${pathname}`;
};

/**
 * Check if a pathname represents a city page
 * @param pathname - The pathname to check
 * @returns True if it's a city page pattern
 */
export const isCityPage = (pathname: string): boolean => {
  const canonicalPath = getCanonicalPath(pathname);
  return /^\/city\/[^\/]+$/.test(canonicalPath);
};

/**
 * Check if a pathname represents a city service page
 * @param pathname - The pathname to check
 * @returns True if it's a city service page pattern
 */
export const isCityServicePage = (pathname: string): boolean => {
  const canonicalPath = getCanonicalPath(pathname);
  return /^\/city\/[^\/]+\/[^\/]+$/.test(canonicalPath);
};

/**
 * Extract city slug from a city page pathname
 * @param pathname - The pathname (e.g., /es/city/madrid or /city/madrid)
 * @returns The city slug or null if not a city page
 */
export const extractCitySlug = (pathname: string): string | null => {
  const canonicalPath = getCanonicalPath(pathname);
  const match = canonicalPath.match(/^\/city\/([^\/]+)/);
  return match ? match[1] : null;
};

/**
 * Extract service slug from a city service page pathname
 * @param pathname - The pathname (e.g., /es/city/madrid/anxiety-therapy)
 * @returns The service slug or null if not a city service page
 */
export const extractServiceSlug = (pathname: string): string | null => {
  const canonicalPath = getCanonicalPath(pathname);
  const match = canonicalPath.match(/^\/city\/[^\/]+\/([^\/]+)$/);
  return match ? match[1] : null;
};